import {
  HttpErrorResponse, HttpHeaders, HttpInterceptorFn, HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { AtualizacaoDisponivelService } from '@core/pwa/atualizacao-disponivel.service';
import { AtualizacaoObrigatoriaService } from '@core/pwa/atualizacao-obrigatoria.service';

/** Versão do SPA que fez a requisição; é por ela que o backend decide se ainda a atende. */
export const HEADER_VERSAO = 'X-Frontend-Versao';

/** Versão mínima que o backend exige; vem em toda resposta da API. */
export const HEADER_VERSAO_MINIMA = 'X-Frontend-Versao-Minima';

/**
 * Versão do SPA publicada agora. Não exige nada: é o que permite oferecer a atualização a quem
 * ainda funciona, sem depender de um manifesto servido pelo CloudFront.
 */
export const HEADER_VERSAO_ATUAL = 'X-Frontend-Versao-Atual';

/** 426 Upgrade Required: o backend recusou a requisição por causa da versão. */
const UPGRADE_REQUIRED = 426;

/**
 * Mantém a versão do SPA em dia com o que o backend exige.
 *
 * <p>Aproveita o tráfego que já existe: toda tela conversa com a gdic-api, então a versão mínima
 * chega de carona nas respostas, sem uma requisição a mais. É o que alcança quem já está com o
 * sistema aberto — o service worker cuida do build, este cabeçalho cuida do contrato.</p>
 *
 * <p>O 426 é a rede de segurança: se o aviso passou despercebido e a versão em uso ficou
 * incompatível de fato, o próprio backend recusa a chamada e a tela bloqueia. Sem isso, um
 * cabeçalho perdido no caminho (proxy, cache) deixaria o usuário operando às cegas.</p>
 *
 * <p>Registrado em app.config.ts via provideHttpClient(withInterceptors(...)).</p>
 */
export const versaoInterceptorFn: HttpInterceptorFn = (req, next) => {
  // Só a API responde sobre versão; assets e requisições externas passam intactos.
  if (!req.url.startsWith(environment.backend)) {
    return next(req);
  }
  const atualizacao = inject(AtualizacaoObrigatoriaService);
  const disponivel = inject(AtualizacaoDisponivelService);

  /**
   * A ordem é deliberada: primeiro o mínimo, que pode bloquear, e só então o convite. Invertida,
   * o aviso opcional apareceria por um instante antes de o bloqueio assumir a tela.
   */
  const conferirVersoes = (cabecalhos: HttpHeaders) => {
    atualizacao.conferir(cabecalhos.get(HEADER_VERSAO_MINIMA));
    disponivel.anunciar(cabecalhos.get(HEADER_VERSAO_ATUAL));
  };

  const requisicao = req.clone({ setHeaders: { [HEADER_VERSAO]: environment.version } });

  return next(requisicao).pipe(
    tap(evento => {
      if (evento instanceof HttpResponse) {
        conferirVersoes(evento.headers);
      }
    }),
    catchError((erro: unknown) => {
      if (erro instanceof HttpErrorResponse) {
        if (erro.status === UPGRADE_REQUIRED) {
          atualizacao.bloquear(erro.headers.get(HEADER_VERSAO_MINIMA) ?? '');
        } else {
          // Erro de negócio também traz os cabeçalhos: um 404 não deixa de ser resposta do servidor.
          conferirVersoes(erro.headers);
        }
      }
      return throwError(() => erro);
    })
  );
};
